///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2020
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { useDatepicker, START_DATE } from "@datepicker-react/hooks";
import { addMonths, format } from "date-fns";
import styled from "styled-components";

import OutlinedInput from "@material-ui/core/OutlinedInput";
import InputLabel from "@material-ui/core/InputLabel";
import { withTheme } from "@material-ui/core/styles";

import StyledFormControl from "../../components/common/StyledFormControl";
import usePrevious from "../../date-picker-utils/usePrevious";
import {
  getDateMonthAndYear,
  getNextActiveMonth,
} from "../../date-picker-utils/getNextActiveMonth";
import {
  getLiveTypePattern,
  getNewValue,
  getInputValueFromStartDate,
  getInputValueFromEndDate,
  setDatesFromLiveTypePattern,
  isInputDelete,
} from "../../date-picker-utils/dateParser";
import Month from "./Month";
import NavButton from "./NavButton";
import DatepickerContext from "./datePickerContext";

const DEFAULT_DATE_FORMAT = "MM/dd/yyyy";

const CalendarContainer = styled.div`
  display: grid;
  margin: 0;
  ${(props) =>
    props.vertical
      ? `grid-template-rows: repeat(${props.activeMonthsLength}, 240px); grid-gap: 5px 32px;`
      : `grid-template-columns: repeat(${props.activeMonthsLength}, 240px); grid-gap: 0 32px;`}
`;

const DatePickerContainer = styled.div`
  display: inline-block;
  position: relative;
  z-index: 95;
  .calendar-svg {
    appearance: none;
    padding-right: 30px;
    background-repeat: no-repeat;
    background-position: calc(100% - 15px) center;
    background-size: auto 1em;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M21.333 2.667H20v2.666h-4V2.667H8v2.666H4V2.667H2.667A2.674 2.674 0 000 5.333v16A2.674 2.674 0 002.667 24h18.666C22.8 24 24 22.8 24 21.333v-16c0-1.466-1.2-2.666-2.667-2.666zm0 18.666H2.667V10.667h18.666v10.666zM7.333 0H4.667v4.667h2.666V0zm12 0h-2.666v4.667h2.666V0z" fill="%2337474F" fillRule="evenodd" /></svg>');
  }
`;

const DropDownContainer = styled.div`
  background-color: #fff;
  position: absolute;
  padding: 32px 32px 36px;
  height: ${(props) => (props.vertical ? "460px" : "300px")};
  z-index: 95;
  -webkit-box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  -moz-box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
`;

const CalendarDivider = styled.div`
  position: absolute;
  width: ${(props) => (props.vertical ? "220px" : "1px")};
  height: ${(props) => (props.vertical ? "1px" : "250px")};
  background: #e6e7e8;
  ${(props) => (props.vertical ? "top: 250px;" : "right: 287px;")}
`;

const StyledOutlinedInput = styled(OutlinedInput)`
  width: 225px;
  color: ${(props) => props.theme.palette.primary.dark} !important;
`;

const DateRangePicker = ({
  handleDateSelect,
  startDate,
  endDate,
  placeholder,
  inputLabel,
  inputLabelWidth,
  isValid,
  disabled,
  minDate,
  maxDate,
  blockedDates,
  shouldBlockDate,
  vertical,
  dateFormat,
  theme,
}) => {
  const [focusedInput, setFocusedInput] = useState(START_DATE);
  const [open, setOpen] = useState(false);
  const [activeMonths, setActiveMonths] = useState(null);

  const dateFormatInternal = dateFormat ? dateFormat : DEFAULT_DATE_FORMAT;
  const [liveTypePattern, setLiveTypePattern] = useState(
    getLiveTypePattern(dateFormatInternal)
  );
  const prevLiveTypePattern = usePrevious(liveTypePattern);
  const [inputValue, setInputValue] = useState("");

  const wrapperRef = useRef(null);

  const handleDateSelectInternal = (startDate, endDate) => {
    handleDateSelect(startDate, endDate);
  };

  const {
    firstDayOfWeek,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateBlocked,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    numberOfMonths,
  } = useDatepicker({
    startDate,
    endDate,
    focusedInput,
    onDatesChange: handleDateChange,
    activeMonths,
    minBookingDate: minDate,
    maxBookingDate: maxDate,
    unavailableDates: blockedDates,
    isDateBlocked: shouldBlockDate,
    firstDayOfWeek:  0 //Start on Sunday
  });

  const goToPreviousMonths = () => {
    setActiveMonths(getNextActiveMonth(activeMonths, numberOfMonths, -1));
  };

  const goToNextMonths = () => {
    setActiveMonths(getNextActiveMonth(activeMonths, numberOfMonths, 1));
  };

  const updateActiveMonthsFromStartDate = () => {
    const date = startDate ? startDate : new Date();
    const nextMonthDate = addMonths(date, 1);
    setActiveMonths([
      getDateMonthAndYear(date),
      getDateMonthAndYear(nextMonthDate),
    ]);
  };

  function handleDateChange(data) {
    if (!data.focusedInput) {
      setFocusedInput(START_DATE);
      handleDateSelectInternal(data.startDate, data.endDate);
      setInputValue(
        getInputValueFromEndDate(
          data.endDate,
          dateFormatInternal,
          liveTypePattern,
          setLiveTypePattern
        )
      );
      setOpen(false);
    } else {
      setFocusedInput(data.focusedInput);
      handleDateSelectInternal(data.startDate, null);
      setInputValue(
        getInputValueFromStartDate(
          data.startDate,
          dateFormatInternal,
          setLiveTypePattern
        )
      );
    }
  }

  const handleFocus = () => {
    setOpen(true);
  };

  const handleClick = () => {
    setOpen(true);
  };

  const handleChange = (event) => {
    const value = event.target.value;
    const isDelete = isInputDelete(liveTypePattern, value);
    const isLastCharNumeric =
      value[value.length - 1] >= "0" && value[value.length - 1] <= "9";
    if (isDelete || isLastCharNumeric)
      setInputValue(
        getNewValue(
          event.target.value,
          liveTypePattern,
          setLiveTypePattern,
          dateFormatInternal
        )
      );
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    setDatesFromLiveTypePattern(
      liveTypePattern,
      prevLiveTypePattern,
      dateFormatInternal,
      focusedInput,
      inputValue,
      isDateBlocked,
      handleDateSelectInternal,
      setOpen,
      setLiveTypePattern,
      setInputValue,
      setFocusedInput
    );
  }, [liveTypePattern]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    updateActiveMonthsFromStartDate();
  }, [startDate]);

  useEffect(() => {
    if (startDate) {
      setInputValue(
        getInputValueFromStartDate(
          startDate,
          dateFormatInternal,
          setLiveTypePattern
        )
      );
    }
    if (startDate && endDate) {
      const startDateString = format(startDate, dateFormatInternal);
      const newLiveTypePattern =
        startDateString +
        liveTypePattern.slice(startDateString.length, liveTypePattern.length);
      setInputValue(
        getInputValueFromEndDate(
          endDate,
          dateFormatInternal,
          newLiveTypePattern,
          setLiveTypePattern
        )
      );
    }
  }, [startDate, endDate]);

  return (
    <DatePickerContainer ref={wrapperRef}>
      <DatepickerContext.Provider
        value={{
          focusedDate,
          isDateSelected,
          isDateHovered,
          isDateBlocked,
          isFirstOrLastSelectedDate,
          onDateSelect,
          onDateFocus,
          onDateHover,
          startDate,
          endDate,
          isDateFocused: () => false,
          minBookingDate: minDate,
          maxBookingDate: maxDate,
          unavailableDates: blockedDates,
        }}
      >
        <StyledFormControl variant="outlined">
          <InputLabel htmlFor="date-picker-input">{inputLabel}</InputLabel>
          <StyledOutlinedInput
            theme={theme}
            type="text"
            id="date-picker-input"
            value={inputValue}
            placeholder={placeholder}
            className="calendar-svg"
            onFocus={handleFocus}
            onChange={handleChange}
            onClick={handleClick}
            error={!isValid}
            disabled={disabled}
            inputProps={{ "data-testid": "datepicker-input" }}
            labelWidth={inputLabelWidth}
          />
        </StyledFormControl>
        {open && (
          <DropDownContainer
            vertical={vertical}
            data-testid="datepicker-dropdown"
          >
            <CalendarDivider vertical={vertical} />
            <NavButton
              onClick={goToNextMonths}
              left={false}
              vertical={vertical}
              data-testid="datepicker-next-month"
            />
            <NavButton
              onClick={goToPreviousMonths}
              left={true}
              data-testid="datepicker-prev-month"
            />
            <CalendarContainer
              activeMonthsLength={activeMonths.length}
              vertical={vertical}
            >
              {activeMonths.map((month) => (
                <Month
                  key={`${month.year}-${month.month}`}
                  year={month.year}
                  month={month.month}
                  firstDayOfWeek={firstDayOfWeek}
                />
              ))}
            </CalendarContainer>
          </DropDownContainer>
        )}
      </DatepickerContext.Provider>
    </DatePickerContainer>
  );
};

DateRangePicker.propTypes = {
  /**
   * The selected startDate
   */
  startDate: PropTypes.instanceOf(Date),
  /**
   * The selected endDate
   */
  endDate: PropTypes.instanceOf(Date),
  /**
   * Function that receives user selected dates with the parameters (startDate, endDate)
   */
  handleDateSelect: PropTypes.func.isRequired,
  /**
   * placeholder for input/hover label
   */
  placeholder: PropTypes.string,
  /**
   * conditional on whether error state
   */
  isValid: PropTypes.bool,
  /**
   * conditional for disabling input
   */
  disabled: PropTypes.bool,
  /**
   * Minimum allowed date
   */
  minDate: PropTypes.instanceOf(Date),
  /**
   * Maximum allowed date
   */
  maxDate: PropTypes.instanceOf(Date),
  /**
   * Array of blocked dates
   */
  blockedDates: PropTypes.arrayOf(Date),
  /**
   * function that takes in a date and returns true/false if it is blocked (setting this overrides all other date blocking)
   */
  shouldBlockDate: PropTypes.func,
  /**
   * Should display in vertical orientation
   */
  vertical: PropTypes.bool,
  /**
   * Date-fns format structure
   */
  dateFormat: PropTypes.string,
};

export default withTheme(DateRangePicker);
